/**
 * Create-session panel for sessions.
 */
import * as React from 'react';
import type { SessionTab } from '@/types/remote/session.types';
import { FieldEventForm } from './session-form-builder/FieldEventForm';
import { InformationMeetingForm } from './session-form-builder/InformationMeetingForm';
import { TutoringSessionForm } from './session-form-builder/TutoringSessionForm';
import { WelcomeMeetingForm } from './session-form-builder/WelcomeMeetingForm';

/** Props for {@link CreateSessionView}. */
interface CreateSessionViewProps {
  sessionType: SessionTab;
  onClose: () => void;
  onSuccess: () => void;
  /** Matching flow: parent handles success/error toasts (pair-specific messages). */
  onError?: (error: unknown) => void;
  /**
   * Pre-fill support for the Matching tab "Confirm pair" flow (§10, §32, §33.7).
   * Extension by optional props only — no rewrite of the component's internal logic.
   */
  initialTutorId?: string;
  initialTutorLabel?: string;
  initialStudentId?: string;
  initialStudentLabel?: string;
  initialSessionDate?: string;
  initialStartTime?: string;
  initialEndTime?: string;
  /** Pair student's program — sent silently, hidden field. */
  initialProgramId?: string;
  /** Active pair UUID — locks tutor/student/managed-by and posts to `/binomes/:id/sessions`. */
  pairId?: string;
  initialManagedById?: string;
  initialManagedByLabel?: string;
}

/** Create-session panel for sessions. */
export const CreateSessionView: React.FC<CreateSessionViewProps> = ({
  sessionType,
  onClose,
  onSuccess,
  onError,
  initialTutorId,
  initialTutorLabel,
  initialStudentId,
  initialStudentLabel,
  initialSessionDate,
  initialStartTime,
  initialEndTime,
  initialProgramId,
  pairId,
  initialManagedById,
  initialManagedByLabel,
}) => {
  if (sessionType === 'tutoring') {
    return (
      <TutoringSessionForm
        mode="create"
        onClose={onClose}
        onSuccess={onSuccess}
        onError={onError}
        initialTutorId={initialTutorId}
        initialTutorLabel={initialTutorLabel}
        initialStudentId={initialStudentId}
        initialStudentLabel={initialStudentLabel}
        initialSessionDate={initialSessionDate}
        initialStartTime={initialStartTime}
        initialEndTime={initialEndTime}
        initialProgramId={initialProgramId}
        pairId={pairId}
        initialManagedById={initialManagedById}
        initialManagedByLabel={initialManagedByLabel}
      />
    );
  }

  if (sessionType === 'information-meeting') {
    return <InformationMeetingForm mode="create" onClose={onClose} onSuccess={onSuccess} />;
  }

  if (sessionType === 'welcome-meeting') {
    return <WelcomeMeetingForm mode="create" onClose={onClose} onSuccess={onSuccess} />;
  }

  if (sessionType === 'field-event') {
    return <FieldEventForm mode="create" onClose={onClose} onSuccess={onSuccess} />;
  }

  return null;
};
